import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StatusBadge from '../components/StatusBadge';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { AlertTriangle, Search, MoreHorizontal, UserPlus, CheckCircle2, Ticket, XCircle, Filter, CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';

const SEVERITIES = [
  { key: 'critical', label: 'Critical', color: 'text-red-600 bg-red-50 border-red-200' },
  { key: 'warning', label: 'Warning', color: 'text-amber-800 bg-amber-50 border-amber-200' },
  { key: 'watchlist', label: 'Watchlist', color: 'text-blue-700 bg-blue-50 border-blue-200' },
  { key: 'informational', label: 'Informational', color: 'text-slate-700 bg-slate-100 border-slate-200' },
];

export default function Alerts() {
  const { alerts, technicians, createTicketFromAlert, updateAlertStatus } = useApp();
  const navigate = useNavigate();
  const [severity, setSeverity] = useState('all');
  const [status, setStatus] = useState('all');
  const [techFilter, setTechFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [q, setQ] = useState('');
  const [date, setDate] = useState();

  const types = useMemo(() => Array.from(new Set(alerts.map((a) => a.type))), [alerts]);

  const filtered = useMemo(() => {
    return alerts.filter((a) => {
      if (severity !== 'all' && a.severity !== severity) return false;
      if (status !== 'all' && a.status !== status) return false;
      if (techFilter !== 'all' && a.assignedTechnician !== techFilter) return false;
      if (typeFilter !== 'all' && a.type !== typeFilter) return false;
      if (q) {
        const query = q.toLowerCase();
        if (!(
          a.customer.toLowerCase().includes(query) ||
          a.address.toLowerCase().includes(query) ||
          a.type.toLowerCase().includes(query)
        )) return false;
      }
      return true;
    });
  }, [alerts, severity, status, techFilter, typeFilter, q]);

  const grouped = useMemo(() => {
    return SEVERITIES.map((s) => ({
      ...s,
      items: filtered.filter((a) => a.severity === s.key),
    }));
  }, [filtered]);

  const handleCreateTicket = (alert) => {
    const t = createTicketFromAlert(alert);
    toast.success(`Ticket ${t.id} created from alert`);
    navigate(`/tickets/${t.id}`);
  };

  return (
    <div className="space-y-5" data-testid="alerts-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Alerts</div>
          <h1 className="mt-1 text-3xl font-semibold text-slate-900 tracking-tight">Alert Management Center</h1>
          <p className="mt-1 text-sm text-slate-500">AI-driven diagnostic signals from the monitored fleet.</p>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9" data-testid="alerts-date-filter">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {date ? date.toDateString() : 'Any date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
            </PopoverContent>
          </Popover>
          <Button variant="outline" className="h-9" onClick={() => { setSeverity('all'); setStatus('all'); setTechFilter('all'); setTypeFilter('all'); setQ(''); setDate(undefined); }}>
            <Filter className="h-4 w-4 mr-2" /> Reset
          </Button>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer, address, alert type..."
              className="pl-9 h-9 bg-slate-50 border-slate-200 focus-visible:bg-white"
              data-testid="alerts-search"
            />
          </div>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger className="h-9" data-testid="filter-severity"><SelectValue placeholder="Severity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="watchlist">Watchlist</SelectItem>
              <SelectItem value="informational">Informational</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={techFilter} onValueChange={setTechFilter}>
            <SelectTrigger className="h-9" data-testid="filter-tech"><SelectValue placeholder="Technician" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All technicians</SelectItem>
              {technicians.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-9 md:w-[320px]" data-testid="filter-type"><SelectValue placeholder="Alert type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {types.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {grouped.map((g) => (
        <div key={g.key} data-testid={`alerts-group-${g.key}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${g.color}`}>
              <AlertTriangle className="h-3.5 w-3.5" /> {g.label}
            </span>
            <span className="font-mono text-xs text-slate-500">{g.items.length} alerts</span>
          </div>
          {g.items.length === 0 ? (
            <Card className="p-6 border-dashed border-slate-200 bg-white/60 text-center text-sm text-slate-500">
              No {g.label.toLowerCase()} alerts matching filters.
            </Card>
          ) : (
            <div className="space-y-3">
              {g.items.map((a) => (
                <Card
                  key={a.id}
                  className={`p-4 border-slate-200 shadow-sm border-l-4 ${g.key === 'critical' ? 'border-l-red-500' : g.key === 'warning' ? 'border-l-amber-500' : g.key === 'watchlist' ? 'border-l-blue-500' : 'border-l-slate-300'}`}
                  data-testid={`alert-card-${a.id}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-base font-semibold text-slate-900">{a.type}</div>
                        <StatusBadge status={a.severity} />
                        <StatusBadge status={a.status} />
                        <span className="font-mono text-[11px] text-slate-500">confidence {a.confidence}%</span>
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        <button onClick={() => navigate(`/customers/${a.customerId}`)} className="font-medium text-[#0052CC] hover:underline">{a.customer}</button>
                        {' · '}{a.address}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500 font-mono">{a.deviceModel} · {a.deviceId} · {a.createdAgo}</div>
                      <div className="mt-3 text-sm text-slate-700">
                        <span className="font-semibold text-slate-900">Detected:</span> {a.detectedIssue}
                      </div>
                      <div className="mt-1 text-sm text-slate-600">{a.aiSummary}</div>
                      <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-2.5 text-xs text-slate-700">
                        <span className="font-semibold text-slate-900">Recommended action:</span> {a.recommended}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 min-w-[180px]">
                      <Button className="bg-[#0052CC] hover:bg-[#0043A6]" onClick={() => handleCreateTicket(a)} data-testid={`alert-ticket-${a.id}`}>
                        <Ticket className="h-4 w-4 mr-2" /> Create Ticket
                      </Button>
                      <Button variant="outline" onClick={() => navigate(`/devices/${a.deviceId}`)}>
                        View Device
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="justify-start">
                            <MoreHorizontal className="h-4 w-4 mr-2" /> More
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { updateAlertStatus(a.id, 'reviewed'); toast.success('Marked reviewed'); }}>
                            <CheckCircle2 className="h-4 w-4 mr-2" /> Mark as Reviewed
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { updateAlertStatus(a.id, 'resolved'); toast.success('Alert resolved'); }}>
                            <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Resolved
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toast.success('Technician reassigned')}>
                            <UserPlus className="h-4 w-4 mr-2" /> Reassign Technician
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { updateAlertStatus(a.id, 'dismissed'); toast.info('Alert dismissed'); }} className="text-red-600 focus:text-red-600">
                            <XCircle className="h-4 w-4 mr-2" /> Dismiss
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
